import { useState, useEffect } from 'react';
import {
    doc,
    onSnapshot,
    setDoc,
    getDoc,
    collection,
    query,
    where,
    getDocs
} from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';
import { db, auth } from '../../Services/firebase-config';

export const useHackathonApplication = (currentTeam, eventId) => {
    const [userApplication, setUserApplication] = useState(null);
    const [evaluationData, setEvaluationData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!currentTeam?.applicationId) {
            setUserApplication(null);
            setEvaluationData(null);
            setIsLoading(false);
            return;
        }

        // Set up real-time listener for application data
        const applicationRef = doc(db, "hackathonApplications", currentTeam.applicationId);

        const unsubscribe = onSnapshot(
            applicationRef,
            (doc) => {
                try {
                    if (doc.exists()) {
                        const applicationData = doc.data();
                        setUserApplication(applicationData);

                        // Update evaluation data if available
                        if (applicationData.isEvaluated && applicationData.evaluation) {
                            setEvaluationData(applicationData.evaluation);
                        } else {
                            setEvaluationData(null);
                        }

                        setError(null);
                    } else {
                        console.log('Application document not found');
                        setUserApplication(null);
                        setEvaluationData(null);
                    }
                } catch (err) {
                    console.error('Error processing application data:', err);
                    setError(err.message);
                } finally {
                    setIsLoading(false);
                }
            },
            (err) => {
                console.error('Error listening to application changes:', err);
                setError(err.message);
                setIsLoading(false);
            }
        );

        return () => unsubscribe();
    }, [currentTeam?.applicationId]);

    // Function to submit application
    const submitApplication = async (applicationData, pitchDeckFile = null) => {
        try {
            let pitchDeckUrl = "";
            let pitchDeckFileName = "";

            // Upload pitch deck file if provided
            if (pitchDeckFile) {
                const { uploadBytes, ref, getDownloadURL } = await import('firebase/storage');
                const { storage } = await import('../../Services/firebase-config');

                const timestamp = Date.now();
                const fileName = `${timestamp}_${pitchDeckFile.name}`;
                const storageRef = ref(
                    storage,
                    `hackathon-applications/${eventId}/${auth.currentUser.uid}/${fileName}`
                );

                await uploadBytes(storageRef, pitchDeckFile);
                pitchDeckUrl = await getDownloadURL(storageRef);
                pitchDeckFileName = pitchDeckFile.name;
            }

            const applicationId = uuidv4();
            const submissionData = {
                id: applicationId,
                ...applicationData,
                pitchDeckUrl,
                pitchDeckFileName,
                socialMediaPosts: applicationData.socialMediaPosts.filter(
                    (post) => post.trim() !== ""
                ),
                eventId: eventId,
                userId: auth.currentUser?.uid,
                isEvaluated: false,
                submissionDate: new Date(),
            };


            delete submissionData.pitchDeckFile;

            // Create application document
            const applicationRef = doc(db, "hackathonApplications", applicationId);
            await setDoc(applicationRef, submissionData);

            return { success: true, applicationId };
        } catch (error) {
            console.error('Error submitting application:', error);
            return { success: false, error: error.message };
        }
    };

    // Function to check if team has already applied (alternative method)
    const checkExistingApplication = async (teamId) => {
        try {
            const applicationsQuery = query(
                collection(db, "hackathonApplications"),
                where("eventId", "==", eventId),
                where("teamId", "==", teamId) // Assuming you store teamId in application
            );

            const snapshot = await getDocs(applicationsQuery);
            return !snapshot.empty ? snapshot.docs[0].data() : null;
        } catch (error) {
            console.error('Error checking existing application:', error);
            return null;
        }
    };

    return {
        userApplication,
        evaluationData,
        isLoading,
        error,
        submitApplication,
        checkExistingApplication,
    };
};